import React from "react";
import { t, Trans } from "@lingui/macro";
import { psidValidationEnv } from "../../constants";
import { Alert } from "../../../../../UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../../../../UI/Button/Button";
import { edmIsEnabled } from "../../../../../../utils/edmIsEnabled";
import { selectUserPosition } from "../../../../../../model/system/selectors";
import { checkExistPSID } from "../../../../../../model/dashboardProducts/dProductsAPI";
import { addNotificationAC } from "../../../../../../model/system/systemActionCreators";
import { getProductOwnerTransComponent } from "../../../../../../constants/productOwner";
import { numberCurrencyFormat } from "../../../../../../constants/convertToLocalCurrency";
import { POSITION_ALLOW_FULL_CRUD_PRODUCTS } from "../../../../../../constants/userPositions";
import { selectProductsDetailItem } from "../../../../../../model/dashboardProducts/selectors";
import {
  TagIcon,
  CodeIcon,
  ScaleIcon,
  PencilIcon,
  FolderIcon,
  QrcodeIcon,
  BookmarkIcon,
  ReceiptTaxIcon,
  CheckCircleIcon,
  ShoppingCartIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";

export type typePopupEdit = {
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
  openPopupEdit: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenPopupEdm: React.Dispatch<React.SetStateAction<boolean>>;
  openDeleteDialog: React.Dispatch<React.SetStateAction<boolean>>;
};

const AddToEdm: React.FC<{
  psid: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenPopupEdm: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ psid, setOpenPopup, setOpenPopupEdm }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);

  const onAddToEdm = async () => {
    setLoading(true);
    const id = new Date();

    try {
      if (psid) {
        const res = await checkExistPSID({ psid });

        if (res.status === 200) {
          setOpenPopup(false);

          setOpenPopupEdm(true);
        } else {
          dispatch(
            addNotificationAC({
              id: id.getTime().toString(),
              message: t`Incorrect PSID. Please make sure that the PSID is correct and try again.`,
            }),
          );
        }
      } else {
        dispatch(
          addNotificationAC({
            id: id.getTime().toString(),
            message: t`Incorrect PSID. Please make sure that the PSID is correct and try again.`,
          }),
        );
      }
    } catch (e) {
      dispatch(
        addNotificationAC({
          id: id.getTime().toString(),
          message: t`Incorrect PSID. Please make sure that the PSID is correct and try again.`,
        }),
      );
    }

    setLoading(false);
  };

  return (
    <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <Button
        type="button"
        disabled={loading}
        onClick={onAddToEdm}
        label={<Trans>Add to Warehouse</Trans>}
      />
    </div>
  );
};

export const PopupDetails: React.FC<typePopupEdit> = ({
  setOpenPopup,
  openPopupEdit,
  setOpenPopupEdm,
  openDeleteDialog,
}) => {
  const position = useSelector(selectUserPosition);
  const dataObject = useSelector(selectProductsDetailItem);

  if (!dataObject) {
    return <Alert type="error" title={<Trans>Have no data.</Trans>} />;
  }

  const psid = psidValidationEnv ? (
    !dataObject!.psid || dataObject!.psid === "invalid" ? (
      <span className=" flex gap-2">
        <Trans>invalid PSID (need to check)</Trans>
        <ExclamationCircleIcon className="text-red-400 w-5 h-5" />
      </span>
    ) : (
      dataObject!.psid
    )
  ) : !dataObject!.psid || dataObject!.psid === "invalid" ? (
    "-"
  ) : (
    dataObject!.psid
  );

  const canDelete =
    position &&
    !dataObject.deleted &&
    POSITION_ALLOW_FULL_CRUD_PRODUCTS.includes(position);

  const canAddToEdm =
    position &&
    edmIsEnabled &&
    !dataObject.deleted &&
    POSITION_ALLOW_FULL_CRUD_PRODUCTS.includes(position);

  const canEdit =
    position &&
    !dataObject.deleted &&
    POSITION_ALLOW_FULL_CRUD_PRODUCTS.includes(position);

  const stores = dataObject.stores.filter((item) => !item.store.deleted);

  return (
    <div>
      <div className="grid gap-x-8 grid-cols-1 sm:grid-cols-2 border-b sm:border sm:rounded-md sm:px-4">
        <div className="flex sm:col-span-2 flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-t-0 sm:border-b ">
          <div className="text-gray-500 text-left">
            <Trans>Product ID:</Trans>
          </div>
          <div className="text-gray-90 text-left">{dataObject.id}</div>
        </div>

        {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
          <div className="flex flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-0">
            <div className="text-gray-500 text-left">
              <Trans>PSID:</Trans>
            </div>
            <div className="text-gray-90 text-left">{psid}</div>
          </div>
        )}
      </div>

      <div className="mt-6 sm:mt-8 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
        <div className="flex flex-wrap gap-x-4 py-2 sm:py-4 border-t border-b sm:border-t-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <PencilIcon className="h-5 w-5 text-gray-500" />
            <Trans>Product name:</Trans>
          </div>
          <div className="text-gray-90 text-left font-medium">
            {dataObject.name}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <FolderIcon className="h-5 w-5 text-gray-500" />
            <Trans>Category name:</Trans>
          </div>
          <div className="text-gray-90 text-left">
            {dataObject.category ? dataObject.category.name : "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <TagIcon className="h-5 w-5 text-gray-500" />
            <Trans>Unit:</Trans>
          </div>
          <div className="text-gray-90 text-left">{dataObject.units}</div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <ScaleIcon className="h-5 w-5 text-gray-500" />
            <Trans>Sold by the piece:</Trans>
          </div>
          <div className="text-gray-90 flex justify-center ">
            {dataObject.integerUnit ? <Trans>Yes</Trans> : <Trans>No</Trans>}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <CodeIcon className="h-5 w-5 text-gray-500" />
            <Trans>
              Unit code
            </Trans>:
          </div>
          <div className="text-gray-90 text-left">
            {dataObject.unitCode ? dataObject.unitCode : "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <CodeIcon className="h-5 w-5 text-gray-500" />
            <Trans>
              Package code
            </Trans>:
          </div>
          <div className="text-gray-90 flex justify-center ">
            {dataObject.packageCode ? dataObject.packageCode : "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 ">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <BookmarkIcon className="h-5 w-5 text-gray-500" />
            <Trans>Marked:</Trans>
          </div>
          <div className="text-gray-90 text-left">
            {dataObject.marked ? <Trans>Yes</Trans> : <Trans>No</Trans>}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 ">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <CheckCircleIcon className="h-5 w-5 text-gray-500" />
            <Trans>Is active:</Trans>
          </div>
          <div className="text-gray-90 text-left">
            {dataObject.active ? <Trans>Yes</Trans> : <Trans>No</Trans>}
          </div>
        </div>

        {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
          <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
            <div className="flex gap-x-2 items-center text-gray-500 text-left">
              <ExclamationCircleIcon className="h-5 w-5 text-gray-500" />
              <Trans>Commission TIN:</Trans>
            </div>
            <div className="text-gray-90 text-left">
              {dataObject.commissionTIN ? dataObject.commissionTIN : "-"}
            </div>
          </div>
        )}
        {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
          <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4">
            <div className="flex gap-x-2 items-center text-gray-500 text-left">
              <ExclamationCircleIcon className="h-5 w-5 text-gray-500" />
              <Trans>Commission PINFL:</Trans>
            </div>
            <div className="text-gray-90 text-left">
              {dataObject.commissionPINFL ? dataObject.commissionPINFL : "-"}
            </div>
          </div>
        )}
        {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
          <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 sm:border-b-0">
            <div className="flex gap-x-2 items-center text-gray-500 text-left">
              <ReceiptTaxIcon className="h-5 w-5 text-gray-500" />
              <Trans>
                VAT
              </Trans>:
            </div>
            <div className="text-gray-90 text-left">
              {dataObject.vat ? dataObject.vat.toFixed(2) : 0} %
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:py-4 sm:border-b-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <ReceiptTaxIcon className="h-5 w-5 text-gray-500" />
            <Trans>
              Product/service owner type
            </Trans>:
          </div>
          <div className="text-gray-90 text-left">
            {dataObject.productOwnerType
              ? getProductOwnerTransComponent(dataObject.productOwnerType)
              : " - "}{" "}
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 mb-2 flex gap-x-2 items-center font-medium">
        <ShoppingCartIcon className="h-5 w-5 text-gray-400" />
        <Trans>Stores:</Trans>
      </div>

      <div className="border-t border-b sm:border overflow-hidden sm:rounded-md">
        {stores.length ? (
          <div className="flex flex-col divide-y">
            {stores.map((item, index) => (
              <div
                key={item.id}
                className={`flex gap-x-6 items-center divided-y ${
                  index % 2 !== 0 ? "sm:bg-gray-50 " : " "
                }`}
              >
                <span className="flex-grow text-left sm:pl-5 py-2 text-md">
                  {item.store.name}
                </span>
                <span className="flex-grow text-right py-2 sm:pr-5 text-md">
                  {numberCurrencyFormat(item.price)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <span className="block py-2 px-4 text-gray-400">
            <Trans>This product is not for sale in any of the stores.</Trans>
          </span>
        )}
      </div>

      {dataObject.barcodes.length ? (
        <>
          <div className="mt-6 sm:mt-8 mb-2 flex gap-x-2 items-center font-medium">
            <QrcodeIcon className="h-5 w-5 text-gray-400" />
            <Trans>Barcodes:</Trans>
          </div>
          <div className="sm:px-4 py-2 border-t border-b sm:border overflow-hidden sm:rounded-md">
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {dataObject.barcodes.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </>
      ) : null}

      <div className="flex flex-wrap gap-x-4 gap-y-2 md:justify-end">
        {canEdit && (
          <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <Button
              type="button"
              label={<Trans>Edit</Trans>}
              onClick={() => {
                setOpenPopup(false);
                openPopupEdit(true);
              }}
            />
          </div>
        )}

        {canAddToEdm && (
          <AddToEdm
            setOpenPopup={setOpenPopup}
            psid={dataObject.psid || ""}
            setOpenPopupEdm={setOpenPopupEdm}
          />
        )}

        {canDelete && (
          <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <Button
              type="button"
              label={<Trans>Delete</Trans>}
              onClick={() => {
                setOpenPopup(false);
                openDeleteDialog(true);
              }}
            />
          </div>
        )}

        <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <Button
            type="button"
            secondary={true}
            label={<Trans>Close</Trans>}
            onClick={() => setOpenPopup(false)}
          />
        </div>
      </div>
    </div>
  );
};
